import React, { useState } from 'react';

interface BrandingLogoProps {
    /**
     * URL to load the logo from. Use a relative `/api/settings/branding/logo`
     * for admin-host surfaces, or an absolute URL when rendering on a
     * different origin (e.g. the patient intake on seadent.es).
     */
    src: string;
    alt?: string;
    /** Tailwind classes applied to the `<img>` element. */
    className?: string;
    /** Rendered instead of the image when no logo is configured (404) or it fails to decode. */
    fallback: React.ReactNode;
}

/**
 * Renders the practice logo from the branding service, falling back to the
 * caller-provided component when no logo is set or the request fails.
 *
 * Patients see this on the intake form; staff see it on the admin login.
 * The image is loaded directly into a regular <img> tag — no auth header
 * required (the branding endpoint is intentionally public for this reason).
 */
const BrandingLogo: React.FC<BrandingLogoProps> = ({ src, alt = 'Logo', className, fallback }) => {
    const [failed, setFailed] = useState(false);
    if (failed) return <>{fallback}</>;
    return (
        <img
            src={src}
            alt={alt}
            className={className}
            onError={() => setFailed(true)}
            loading="eager"
            decoding="async"
        />
    );
};

export default BrandingLogo;
